/**
 * Composite Adapter
 * Sends events to multiple adapters simultaneously
 */

import type { AnalyticsAdapter } from './adapter.interface'
import type {
  AdapterConfig,
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
} from '../../domain/types'

/**
 * Composite adapter
 * Forwards events to multiple adapters
 */
export class CompositeAdapter implements AnalyticsAdapter {
  readonly name = 'composite'

  private adapters: AnalyticsAdapter[] = []

  constructor(adapters: AnalyticsAdapter[] = []) {
    this.adapters = adapters
  }

  /**
   * Add an adapter to the composite
   */
  addAdapter(adapter: AnalyticsAdapter): void {
    this.adapters.push(adapter)
  }

  /**
   * Remove an adapter from the composite
   */
  removeAdapter(name: string): void {
    this.adapters = this.adapters.filter((a) => a.name !== name)
  }

  /**
   * Get adapter by name
   */
  getAdapter(name: string): AnalyticsAdapter | undefined {
    return this.adapters.find((a) => a.name === name)
  }

  private adapterConfigs: Map<string, AdapterConfig> = new Map()

  /**
   * Set config for a specific adapter by name
   */
  setAdapterConfig(adapterName: string, config: AdapterConfig): void {
    this.adapterConfigs.set(adapterName, config)
  }

  async initialize(config: AdapterConfig): Promise<void> {
    // Initialize all adapters with their specific config or fallback
    await Promise.all(
      this.adapters.map(async (adapter) => {
        try {
          const adapterConfig = this.adapterConfigs.get(adapter.name) || config
          await adapter.initialize(adapterConfig)
        } catch (error) {
          console.error(`[Analytics] Failed to initialize ${adapter.name}:`, error)
        }
      })
    )
  }

  trackPageView(event: PageViewEvent): void {
    this.forwardToAdapters('trackPageView', event)
  }

  track(event: TrackEvent): void {
    this.forwardToAdapters('track', event)
  }

  identify(event: IdentifyEvent): void {
    this.forwardToAdapters('identify', event)
  }

  trackError(event: ErrorEvent): void {
    this.forwardToAdapters('trackError', event)
  }

  trackPerformance(event: PerformanceEvent): void {
    this.forwardToAdapters('trackPerformance', event)
  }

  reset(): void {
    this.adapters.forEach((adapter) => {
      try {
        adapter.reset()
      } catch (error) {
        console.error(`[Analytics] ${adapter.name} reset error:`, error)
      }
    })
  }

  async flush(): Promise<void> {
    await Promise.all(
      this.adapters.map(async (adapter) => {
        try {
          await adapter.flush()
        } catch (error) {
          console.error(`[Analytics] ${adapter.name} flush error:`, error)
        }
      })
    )
  }

  isReady(): boolean {
    return this.adapters.length > 0 && this.adapters.some((a) => a.isReady())
  }

  setConsent(granted: boolean): void {
    this.adapters.forEach((adapter) => {
      try {
        adapter.setConsent(granted)
      } catch (error) {
        console.error(`[Analytics] ${adapter.name} setConsent error:`, error)
      }
    })
  }

  /**
   * Forward method call to all adapters
   */
  private forwardToAdapters<T extends keyof AnalyticsAdapter>(
    method: T,
    ...args: Parameters<AnalyticsAdapter[T] extends (...args: infer P) => unknown ? (...args: P) => unknown : never>
  ): void {
    this.adapters.forEach((adapter) => {
      try {
        const fn = adapter[method] as (...args: unknown[]) => void
        fn.apply(adapter, args)
      } catch (error) {
        console.error(`[Analytics] ${adapter.name} ${method} error:`, error)
      }
    })
  }
}
